import { z } from 'zod'
import { sdkSchemas } from '@/sdk'
import type { TKey } from '@/translations/i18n'

/**
 * Validation schema shared by the create and edit forms. Covers only the event
 * fields; the invitee CSV is a `File` handled outside the form (create-only) and
 * validated separately, so it isn't part of this schema.
 *
 * Three independent dimensions, one control each: `format` (the Salesforce
 * picklist, defaults to Conference), `captureMode` (presentation / tune-up,
 * create-only — the API rejects it on update) and `eventType` (in-person /
 * virtual, `null` on events created before the field existed).
 *
 * `ctas` is a multiselect of CTA codes submitted as one array (first = lead),
 * required only in `presentation`: a tune-up captures per attendee as well, so
 * its blanket selection may stay empty. An empty one is then sent as `[]` on
 * update (which is how PATCH empties it) and **omitted** on create, which
 * rejects `[]` 422 in both modes.
 * Error messages are `marquee` i18n keys, resolved with `t()` at render time.
 */
export const EventFormSchema = z
	.object({
		name: z.string().min(1, { message: 'marquee:error_name_required' }),
		market: z.string().min(1, { message: 'marquee:error_market_required' }),
		date: z.string().min(1, { message: 'marquee:error_date_required' }),
		startTime: z
			.string()
			.min(1, { message: 'marquee:error_start_time_required' }),
		endTime: z
			.string()
			.min(1, { message: 'marquee:error_end_time_required' }),
		// Every enum comes from the SDK's generated zod rather than local
		// literals, so a backend picklist change lands on the next
		// `pnpm sdk:generate`.
		format: sdkSchemas.zMarqueeEventFormat.optional(),
		captureMode: sdkSchemas.zMarqueeCaptureMode,
		// Nullable, like the read model: a legacy event edits with no event type
		// selected rather than being silently rewritten to in-person.
		eventType: sdkSchemas.zMarqueeEventListItem.shape.event_type,
		hashtag: z
			.string()
			// "#" alone is the field's anchor, not a real value — require the "#"
			// plus at least one more char (trimmed), else it's treated as empty.
			.refine((value) => value.trim().length >= 2, {
				message: 'marquee:error_hashtag_required'
			})
			.refine((value) => value.startsWith('#'), {
				message: 'marquee:error_hashtag_format'
			}),
		ctas: z.array(z.string()),
		marketManagerIds: z
			.array(z.string())
			.min(1, { message: 'marquee:error_market_managers_required' })
	})
	.superRefine((data, ctx) => {
		// A tune-up captures CTAs per attendee, so its blanket selection may
		// stay empty; a presentation must always carry at least one.
		if (data.captureMode === 'presentation' && data.ctas.length === 0) {
			ctx.addIssue({
				code: 'custom',
				path: ['ctas'],
				message: 'marquee:error_ctas_required'
			})
		}
	})

export type EventFormValues = z.input<typeof EventFormSchema>

/**
 * Add / edit form of the Team screen, shared by both modes.
 *
 * Mirrors the operator admin API's own checks so the common mistakes are
 * caught before the round trip: both names (1–100 characters, sent together),
 * an assignable role (`admin` is not one), and a Salesforce **User** id while
 * the market-manager switch is on. The email domain is the server's call (422
 * `domain-not-allowed`).
 *
 * The id's shape is held here on purpose: the spec carries no `pattern` for it,
 * because a schema pattern would answer before the backend's own check and
 * swallow its "this is an Account id" message. This is instant feedback only;
 * the backend still validates on save, and its 422 lands on the field. A `001…`
 * value gets its own message, checked first, since that is the Account id
 * people paste by mistake.
 */
const SF_USER_ID_RE = /^005[A-Za-z0-9]{15}$/
const SF_ACCOUNT_ID_PREFIX = '001'
const NAME_MAX_LENGTH = 100

const operatorName = (requiredMessage: TKey) =>
	z
		.string()
		.trim()
		.min(1, { message: requiredMessage })
		.max(NAME_MAX_LENGTH, { message: 'marquee:error_team_name_too_long' })

export const TeamMemberFormSchema = z
	.object({
		firstName: operatorName('marquee:error_team_first_name_required'),
		lastName: operatorName('marquee:error_team_last_name_required'),
		email: z
			.string()
			.trim()
			.min(1, { message: 'marquee:error_team_email_required' })
			.pipe(z.email({ message: 'marquee:error_team_email_invalid' })),
		role: z.enum(sdkSchemas.zMarqueeAssignableOperatorRole.options, {
			message: 'marquee:error_team_role_required'
		}),
		isMarketManager: z.boolean(),
		sfUserId: z.string().trim()
	})
	.superRefine((data, ctx) => {
		if (!data.isMarketManager) {
			return
		}
		const issue = (message: TKey) =>
			ctx.addIssue({ code: 'custom', path: ['sfUserId'], message })
		if (!data.sfUserId) {
			issue('marquee:error_team_sf_user_id_required')
		} else if (data.sfUserId.startsWith(SF_ACCOUNT_ID_PREFIX)) {
			issue('marquee:error_team_sf_user_id_account_id')
		} else if (!SF_USER_ID_RE.test(data.sfUserId)) {
			issue('marquee:error_team_sf_user_id_format')
		}
	})

export type TeamMemberFormValues = z.input<typeof TeamMemberFormSchema>

/**
 * Add / edit form of the Locations screen, shared by both modes.
 *
 * Name, parent market and time zone are required (OPSSTD-10100); the name's
 * length limit is the contract's own, read off the generated schema. `country`
 * only narrows the market picker and is never sent.
 */
const LOCATION_NAME = sdkSchemas.zMarqueeLocationCreate.shape.name

export const LocationFormSchema = z.object({
	name: z
		.string()
		.trim()
		.min(1, { message: 'marquee:error_location_name_required' })
		.refine((value) => LOCATION_NAME.safeParse(value).success, {
			message: 'marquee:error_location_name_too_long'
		}),
	country: z.string(),
	marketId: z
		.string()
		.min(1, { message: 'marquee:error_location_market_required' }),
	timeZone: z
		.string()
		.min(1, { message: 'marquee:error_location_time_zone_required' })
})

export type LocationFormValues = z.input<typeof LocationFormSchema>
